import { Icon } from '@iconify/react';
import { Box, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FundLogo } from '../../../../components/custom/FundLogo';
import StatusCircle from '../../../../components/custom/StatusCircle';
import BoxesIcon from '../../../../components/icons/BoxesIcon';
import BoxIcon from '../../../../components/icons/BoxIcon';
import FileIcon from '../../../../components/icons/FileIcon';

const sidebarVariants = {
  hidden: { x: '-100%', transition: { duration: 0.5 } },
  visible: { x: 0, transition: { duration: 0.5 } },
};

const FundsSidebar = ({
  open,
  toggleSidebar,
  fundName,
  products,
}: {
  open: boolean;
  toggleSidebar: () => void;
  fundName: string;
  products: any;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isClosing, setIsClosing] = useState(false);

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        toggleSidebar();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <motion.div
      ref={sidebarRef}
      initial="hidden"
      animate={open && !isClosing ? 'visible' : 'hidden'}
      exit="hidden"
      variants={sidebarVariants}
      style={{
        width: '500px',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#FFF',
        zIndex: 1200,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          py: '45px',
          px: '25px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            boxShadow: '0px 0px 50px 0px #00000040',
            flexDirection: 'column',
            gap: '20px',
            py: '20px',
            px: '30px',
            borderRadius: '30px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Icon
              style={{ cursor: 'pointer' }}
              icon={'material-symbols:close'}
              onClick={toggleSidebar}
            />
          </Box>
          <Stack px="60px" spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <FundLogo width="100px" height="50px" logoIndex={fundName} />
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ background: '#F2F4F7', borderRadius: '10px', p: '8px' }}
              >
                <BoxesIcon />
                <Typography
                  fontSize="18px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  {products.length} מוצרים
                </Typography>
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <FileIcon />
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  סך הכל: 0
                </Typography>
              </Stack>
              <Stack direction="row" flex={1} justifyContent={'space-between'}>
                <Stack direction="row" alignItems="center">
                  <Box ml={0.5}>
                    <StatusCircle status="COMPLETED" />
                  </Box>
                  <Typography
                    fontSize="14px"
                    fontWeight="700"
                    fontFamily={'AlmoniDL'}
                  >
                    הצלחה:&nbsp;
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontWeight="700"
                    fontFamily={'AlmoniDL'}
                  >
                    0
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Box ml={0.5}>
                    <StatusCircle status="IN_PROGRESS" />
                  </Box>
                  <Typography
                    fontSize="14px"
                    fontWeight="700"
                    fontFamily={'AlmoniDL'}
                  >
                    בתהליך:&nbsp;
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontWeight="700"
                    fontFamily={'AlmoniDL'}
                  >
                    0
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Box ml={0.5}>
                    <StatusCircle status="REJECTED" />
                  </Box>
                  <Typography
                    fontSize="14px"
                    fontWeight="700"
                    fontFamily={'AlmoniDL'}
                  >
                    כישלון:&nbsp;
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontWeight="700"
                    fontFamily={'AlmoniDL'}
                  >
                    0
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Stack spacing={2}>
          {products.map((item: any) => (
            <Item key={item.id} product={item} />
          ))}
        </Stack>
      </Box>
    </motion.div>
  );
};

export default FundsSidebar;

const Item = ({ product }: { product: any }) => {
  const extractNumbers = (str: string | undefined | { S?: string }): string => {
    if (!str) {
      return '';
    }

    if (typeof str === 'object' && str.S) {
      return str.S.replace(/[^0-9]/g, '');
    }

    if (typeof str === 'string') {
      return str.replace(/[^0-9]/g, '');
    }

    return '';
  };

  const formatBankNumber = (bank: any, branch: any, account: any) => {
    const bankNum = extractNumbers(bank);
    const branchNum = extractNumbers(branch);
    const accountNum = extractNumbers(account);

    return `${bankNum}.${branchNum}.${accountNum}`;
  };

  const bankNumber = formatBankNumber(
    product.fundDetails.bank,
    product.fundDetails.branch,
    product.fundDetails.bankAccount,
  );

  return (
    <Stack
      spacing={2}
      sx={{ background: '#F2F4F7', p: '27px', borderRadius: '20px' }}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <BoxIcon />
        <Typography fontSize="24px" fontWeight="700" fontFamily={'AlmoniDL'}>
          {product.fundDetails.productName?.S ||
            product.fundDetails.productName ||
            ''}
        </Typography>
      </Stack>
      <Stack>
        <Typography fontSize="18px" fontWeight="700" fontFamily={'AlmoniDL'}>
          חשבון בנק
        </Typography>
        <Typography fontSize="18px" fontWeight="400" fontFamily={'AlmoniDL'}>
          {bankNumber}
        </Typography>
      </Stack>
      <Stack>
        <Typography fontSize="18px" fontWeight="700" fontFamily={'AlmoniDL'}>
          מספר קופה
        </Typography>
        <Typography fontSize="18px" fontWeight="400" fontFamily={'AlmoniDL'}>
          {product.fund}
        </Typography>
      </Stack>
    </Stack>
  );
};
